import React, { useEffect, useMemo, useState, Fragment, createElement, Component } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { PromoStrip } from './components/PromoStrip';
import { PigeonAPI } from './components/PigeonAPI';
import { SecuritySection } from './components/SecuritySection';
import { Footer } from './components/Footer';
import { SubjectivitiesNavbar } from './components/SubjectivitiesNavbar';
import { BrandedEmailInterface } from './components/BrandedEmailInterface';
import { SuccessPage } from './components/SuccessPage';
import { isMobile } from './utils/deviceDetection';
import { ArrowRightIcon, CheckIcon, UploadIcon, CheckCircleIcon, CloudIcon, ClockIcon, BarChartIcon, ShieldCheckIcon, FileTextIcon, FileIcon, PenIcon, MessageSquareIcon, DatabaseIcon, LockIcon } from 'lucide-react';
export function App() {
  // Add Inter font
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  // Add tracking script
  useEffect(() => {
    // Check if script already exists to avoid duplicates
    if (window.reb2b) return;
    const script = document.createElement('script');
    script.innerHTML = `!function(key) {if (window.reb2b) return;window.reb2b = {loaded: true};var s = document.createElement("script");s.async = true;s.src = "https://b2bjsstore.s3.us-west-2.amazonaws.com/b/" + key + "/" + key + ".js.gz";document.getElementsByTagName("script")[0].parentNode.insertBefore(s, document.getElementsByTagName("script")[0]);}("QO92DHKE84N7");`;
    script.async = true;
    document.head.appendChild(script);
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);
  // Use debounced scroll for mobile
  const {
    scrollY
  } = useScroll({
    // Use a more efficient scroll handling on mobile
    container: undefined,
    offset: ['start', 'end'],
    layoutEffect: !isMobile()
  });
  const opacity = useTransform(scrollY, [0, 100], [1, 0]);
  // State for How It Works slider
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [{
    title: 'Pigeon API automatically sends an email requesting relevant subjectivities',
    subtitle: '',
    image: 'https://i.imgur.com/PCRA5dA.png'
  }, {
    title: 'Not using our API? Or making an ad-hoc request?',
    subtitle: 'Send requests in a few clicks with our Outlook Plugin or Web App',
    image: 'https://i.imgur.com/rraIn8t.png'
  }, {
    title: 'Producers can either:\n1) Submit via the link, no account needed',
    subtitle: '',
    image: 'https://i.imgur.com/HYIP3HH.png'
  }, {
    title: 'or 2) Reply with attachments, which are automatically categorized and submitted',
    subtitle: '',
    image: 'https://i.imgur.com/j4AWz7l.png'
  }, {
    title: 'Automatic reminders are sent until completion',
    subtitle: '',
    image: 'https://i.imgur.com/bxfZbFI.png'
  }, {
    title: 'Submissions are reviewed and verified with\nAI-Assisted Subjectivity Review',
    subtitle: '',
    image: 'https://i.imgur.com/vZtDIUt.png',
    hasGradient: true
  }, {
    title: 'Files are automatically transferred to your document management system',
    subtitle: '',
    image: 'https://i.imgur.com/pC4rwrL.png'
  }];
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % slides.length);
  };
  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  };
  return <BrowserRouter data-id="element-0">
      <Routes data-id="element-1">
        <Route path="/" element={<div className="font-[Inter,ui-sans-serif,system-ui] text-[#0B1220] overflow-x-hidden relative bg-gradient-to-b from-[#f8faff] to-[#f0f5ff]" data-id="element-3">
              {/* Cinematic frame markers - hidden on mobile for performance */}
              {!isMobile() && <>
                  <div className="fixed inset-x-0 top-0 h-[2px] bg-[#2E6BFF]/5 z-50" data-id="element-4"></div>
                  <div className="fixed inset-x-0 bottom-0 h-[2px] bg-[#2E6BFF]/5 z-50" data-id="element-5"></div>
                  <div className="fixed inset-y-0 left-0 w-[2px] bg-[#2E6BFF]/5 z-50" data-id="element-6"></div>
                  <div className="fixed inset-y-0 right-0 w-[2px] bg-[#2E6BFF]/5 z-50" data-id="element-7"></div>
                </>}
              {/* Dynamic background elements - simplified for mobile */}
              <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none" data-id="element-8">
                <div className="absolute top-0 right-0 w-[80vw] h-[80vh] rounded-full bg-gradient-to-b from-[#2E6BFF]/5 to-transparent opacity-40 blur-[120px]" data-id="element-9"></div>
                <div className="absolute bottom-0 left-0 w-[70vw] h-[70vh] rounded-full bg-gradient-to-t from-[#36D1DC]/5 to-transparent opacity-40 blur-[100px]" data-id="element-10"></div>
                {/* Simplified animation for mobile */}
                <motion.div className="absolute top-1/4 left-1/4 w-[50vw] h-[50vh] rounded-full bg-gradient-to-r from-[#0BB489]/5 to-transparent opacity-30 blur-[80px]" animate={isMobile() ? {
            opacity: [0.2, 0.3, 0.2]
          } // Simpler animation for mobile
          : {
            x: [0, 20, 0],
            y: [0, -20, 0]
          } // Full animation for desktop
          } transition={{
            duration: isMobile() ? 10 : 20,
            repeat: Infinity,
            repeatType: 'reverse',
            ease: 'easeInOut'
          }} style={{
            willChange: isMobile() ? 'opacity' : 'transform' // Optimize what changes
          }} data-id="element-11"></motion.div>
              </div>
              <Navbar data-id="element-12" />
              <SubjectivitiesNavbar data-id="element-13" />
              <PromoStrip data-id="element-14" />
              <main className="relative z-10" data-id="element-15">
                {/* Hero Section */}
                <section className="min-h-[70vh] flex flex-col justify-center pt-24 pb-8 px-4 sm:px-6 lg:px-8 relative overflow-x-hidden bg-[#f8faff]" data-id="element-16">
                  <div className="max-w-7xl mx-auto w-full flex flex-col flex-grow justify-center my-auto" data-id="element-17">
                    <div className="grid md:grid-cols-2 gap-8 items-center relative z-10" data-id="element-18">
                      <motion.div initial={{
                  opacity: 0,
                  y: isMobile() ? 10 : 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: isMobile() ? 0.5 : 0.7
                }} className="text-center md:text-left flex flex-col" data-id="element-19">
                        <motion.h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-full" initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 0.7,
                    delay: 0.2
                  }} data-id="element-20">
                          <span className="bg-gradient-to-r from-[#0B1220] to-[#2E6BFF] bg-clip-text text-transparent" data-id="element-21">
                            Stop managing subjectivities
                          </span>
                          <br data-id="element-22" />
                          <span className="bg-gradient-to-r from-[#2E6BFF] to-[#36D1DC] bg-clip-text text-transparent" data-id="element-23">
                            manually.
                          </span>
                        </motion.h1>
                        <motion.p className="mt-5 text-lg sm:text-xl text-[#5A6376] max-w-xl mx-auto md:mx-0" initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 0.7,
                    delay: 0.4
                  }} data-id="element-24">
                          Automate your process and save thousands of hours.
                        </motion.p>
                        <motion.div className="mt-5" initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} transition={{
                    duration: 0.7,
                    delay: 0.8
                  }} data-id="element-25">
                          <div className="flex flex-col items-center md:items-start" data-id="element-26">
                            <motion.a href="https://calendly.com/pigeondocuments" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-6 py-3.5 text-base font-medium rounded-full text-white relative overflow-hidden shadow-[0_8px_16px_rgba(46,107,255,0.25)] w-fit" whileHover={{
                        y: -2,
                        boxShadow: '0 12px 20px rgba(46,107,255,0.3)'
                      }} whileTap={{
                        scale: 0.98
                      }} style={{
                        background: 'linear-gradient(to right, #2E6BFF, #36D1DC)'
                      }} data-id="element-27">
                              <span className="relative z-10" data-id="element-28">Book a Call</span>
                              <ArrowRightIcon className="ml-2 h-4 w-4 relative z-10" data-id="element-29" />
                              <div className="absolute inset-0 transition-all duration-300 opacity-0 hover:opacity-100" style={{
                          background: 'linear-gradient(to right, #2059E2, #32C3CE)'
                        }} data-id="element-30" />
                            </motion.a>
                          </div>
                        </motion.div>
                      </motion.div>
                      <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  duration: 1
                }} className="relative" data-id="element-31">
                        {/* Image Placeholder - Same as main page */}
                        <motion.div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden border border-white/50 w-full" style={{
                    height: 'auto',
                    maxHeight: {
                      lg: '450px'
                    }
                  }} whileHover={{
                    boxShadow: '0 20px 40px rgba(9,16,29,0.12)',
                    y: -5
                  }} transition={{
                    duration: 0.5
                  }} data-id="element-32">
                          <div className="w-full h-full flex flex-col p-0" data-id="element-33">
                            {/* Email content */}
                            <BrandedEmailInterface data-id="element-34" />
                          </div>
                        </motion.div>
                      </motion.div>
                    </div>
                  </div>
                </section>
                {/* Spacer between hero and companies */}
                <div className="w-full h-6 bg-[#f8faff]" data-id="element-35"></div>
                {/* Companies Section - Moved above ticker */}
                <div className="max-w-7xl mx-auto w-full bg-[#f0f5ff] py-8 px-4 sm:px-6 lg:px-8" data-id="element-36">
                  <div className="max-w-7xl mx-auto" data-id="element-37">
                    <motion.h2 initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.7
              }} className="text-3xl sm:text-4xl font-bold text-center mb-12" data-id="element-38">
                      Trusted by leading companies
                    </motion.h2>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8" data-id="element-39">
                      {[{
                  name: 'Gridiron Insurance',
                  logo: 'https://i.imgur.com/Jc7z8B3.png'
                }, {
                  name: 'Wholesure',
                  logo: 'https://cdn.magicpatterns.com/uploads/2z7RvLsZkZK8z5nsD2j9n2/YTmXmIx.png'
                }, {
                  name: 'SBLI',
                  logo: 'https://i.imgur.com/u9XTdqO.png'
                }, {
                  name: 'And More',
                  logo: 'https://i.imgur.com/NDSrBQH.png'
                }].map((company, i) => <motion.div key={i} initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: i * 0.1,
                  duration: 0.5
                }} className="bg-white rounded-xl shadow-sm px-4 py-2 flex items-center justify-center h-20" data-id="element-40">
                          <img src={company.logo} alt={company.name} className="max-h-[95%] max-w-[95%] object-contain" data-id="element-41" />
                        </motion.div>)}
                      <motion.div initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: 0.4,
                  duration: 0.5
                }} className="bg-white rounded-xl shadow-sm px-4 py-2 flex items-center justify-center h-20" data-id="element-42">
                        <span className="text-xl font-medium text-[#0B1220]" data-id="element-43">
                          + and more
                        </span>
                      </motion.div>
                    </div>
                  </div>
                </div>
                {/* Customer Value Section */}
                <section id="customer-value" className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f8faff]" data-id="element-44">
                  <div className="max-w-7xl mx-auto" data-id="element-45">
                    <div className="text-center mb-16" data-id="element-46">
                      <motion.h2 initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.7
                }} className="text-3xl sm:text-4xl font-bold mb-4 text-[#0B1220]" data-id="element-47">
                        Save{' '}
                        <span className="bg-gradient-to-r from-[#2E6BFF] to-[#36D1DC] bg-clip-text text-transparent" data-id="element-48">
                          60 minutes
                        </span>{' '}
                        per policy
                      </motion.h2>
                      <motion.p initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: 0.1,
                  duration: 0.7
                }} className="text-xl text-[#5A6376] max-w-3xl mx-auto" data-id="element-49">
                        Pigeon eliminates all of your manual subjectivity
                        collection tasks
                      </motion.p>
                    </div>
                    <div className="grid md:grid-cols-2 gap-8 lg:gap-12" data-id="element-50">
                      {/* Without Pigeon */}
                      <motion.div initial={{
                  opacity: 0,
                  x: -20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.7
                }} className="bg-white rounded-2xl shadow-md overflow-hidden" data-id="element-51">
                        <div className="bg-[#d9413f] text-white p-4 flex flex-row items-center justify-center" data-id="element-52">
                          <h3 className="text-2xl sm:text-3xl font-bold mr-3" data-id="element-53">
                            Without Pigeon
                          </h3>
                          <span className="bg-white text-[#d9413f] px-6 py-2 rounded-lg text-xl font-bold" data-id="element-54">
                            65 min
                          </span>
                        </div>
                        <div className="p-6 space-y-5" data-id="element-55">
                          {[{
                      time: '10 min',
                      task: 'Send Initial Email to Producer'
                    }, {
                      time: '5 min',
                      task: 'Create Follow-Up Reminder'
                    }, {
                      time: '10 min',
                      task: 'Track Incoming Documents'
                    }, {
                      time: '10 min',
                      task: 'Send Follow-Up Emails'
                    }, {
                      time: '10 min',
                      task: 'Review Documents'
                    }, {
                      time: '10 min',
                      task: 'Request Corrections'
                    }, {
                      time: '10 min',
                      task: 'Download & Organize Documents'
                    }].map((item, index) => <div key={index} className="grid grid-cols-[120px_1fr] gap-4 items-center" data-id="element-56">
                              <div className="w-full" data-id="element-57">
                                <span className="px-4 py-2 bg-[#d9413f] text-white rounded-md text-base font-medium w-full block text-center" data-id="element-58">
                                  {item.time}
                                </span>
                              </div>
                              <span className="text-[#0B1220] text-base" data-id="element-59">
                                {item.task}
                              </span>
                            </div>)}
                        </div>
                      </motion.div>
                      {/* With Pigeon */}
                      <motion.div initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.7
                }} className="bg-white rounded-2xl shadow-md overflow-hidden" data-id="element-60">
                        <div className="bg-[#4285f4] text-white p-4 flex flex-row items-center justify-center" data-id="element-61">
                          <h3 className="text-2xl sm:text-3xl font-bold flex items-center mr-3" data-id="element-62">
                            With{' '}
                            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pq5SdjVQDXRRHfq5V6gwps/Pigeon_White.png" alt="Pigeon Logo" className="h-6 mx-2" data-id="element-63" />{' '}
                            Pigeon
                          </h3>
                          <span className="bg-white text-[#4285f4] px-6 py-2 rounded-lg text-xl font-bold" data-id="element-64">
                            5 min
                          </span>
                        </div>
                        <div className="p-6 space-y-5 bg-[#f0f7ff]" data-id="element-65">
                          {[{
                      time: 'Automated',
                      task: 'Send Initial Email to Producer',
                      automated: true
                    }, {
                      time: 'Automated',
                      task: 'Create Follow-Up Reminder',
                      automated: true
                    }, {
                      time: 'Automated',
                      task: 'Track Incoming Documents',
                      automated: true
                    }, {
                      time: 'Automated',
                      task: 'Send Follow-Up Emails',
                      automated: true
                    }, {
                      time: '5 min',
                      task: 'Review Documents',
                      aiAssisted: true
                    }, {
                      time: 'Automated',
                      task: 'Request Corrections',
                      automated: true
                    }, {
                      time: 'Automated',
                      task: 'Download & Organize Documents',
                      automated: true
                    }].map((item, index) => <div key={index} className="grid grid-cols-[120px_1fr] gap-4 items-center" data-id="element-66">
                              <div className="w-full" data-id="element-67">
                                <span className={`px-4 py-2 ${item.time === '5 min' ? 'bg-[#4285f4]' : 'bg-gradient-to-r from-[#2E6BFF] to-[#36D1DC]'} text-white rounded-md text-base font-medium w-full block text-center flex items-center justify-center`} data-id="element-68">
                                  {item.time}
                                </span>
                              </div>
                              <div className="flex items-center" data-id="element-69">
                                <span className={`text-base ${item.automated ? 'text-[#a0a0a0] line-through italic' : 'text-[#0B1220]'}`} data-id="element-70">
                                  {item.task}
                                </span>
                                {item.aiAssisted && <span className="bg-[#5a6bed] text-white text-xs font-medium italic rounded-md px-3 py-1 ml-3 flex items-center" data-id="element-71">
                                    <span className="mr-1" data-id="element-72">✨</span> AI Assisted
                                  </span>}
                              </div>
                            </div>)}
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </section>
                {/* Ticker Section */}
                <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f0f5ff]" data-id="element-73">
                  <div className="max-w-7xl mx-auto w-full overflow-hidden" data-id="element-74">
                    <div className="relative" data-id="element-75">
                      <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  duration: 1
                }} className="text-center mb-10" data-id="element-76">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0B1220] mb-6" data-id="element-77">
                          Any line, any requirement
                          <br data-id="element-78" />
                          collected and verified automatically
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto" data-id="element-79">
                          {['Commercial P&C', 'Financial & Professional', 'Specialty and E&S', '+\nand more'].map((category, index) => <div key={`category-${index}`} className="bg-white/80 backdrop-blur-sm rounded-lg border border-[#E7EAF0] shadow-sm p-4 flex items-center justify-center" data-id="element-80">
                              <span className="font-medium text-[#0B1220] text-center whitespace-pre-line" data-id="element-81">
                                {category}
                              </span>
                            </div>)}
                        </div>
                      </motion.div>
                      <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  duration: 1
                }} className="ticker-container overflow-hidden relative w-full" data-id="element-82">
                        {/* Left fade overlay */}
                        <div className="absolute left-0 top-0 bottom-0 w-16 z-10 pointer-events-none bg-gradient-to-r from-[#f0f5ff] to-transparent" data-id="element-83"></div>
                        <div className="ticker-wrapper whitespace-nowrap inline-block" data-id="element-84">
                          {useMemo(() => {
                      // Updated list with exact text as specified
                      const tickerItems = ['TRIA Forms', 'Signed & Dated Applications', 'Underlying Policies', 'Loss History', 'Surplus Lines Disclosure Forms', 'Inspection Reports', 'Final Layer Structure Outlines', 'Statement of Values (SOVs)', 'Supplemental Applications', 'UM Forms', 'No Known Loss Letters (NKLLs)', 'And More'];
                      return <>
                                {tickerItems.map((item, index) => <div key={`ticker-item-${index}`} className="inline-block mx-2 bg-white/80 px-4 py-2 rounded-full border border-[#E7EAF0]" data-id="element-85">
                                    <span className="text-sm font-medium text-[#5A6376]" data-id="element-86">
                                      {item}
                                    </span>
                                  </div>)}
                                {/* Duplicated set for seamless looping */}
                                {tickerItems.map((item, index) => <div key={`ticker-item-duplicate-${index}`} className="inline-block mx-2 bg-white/80 px-4 py-2 rounded-full border border-[#E7EAF0]" data-id="element-87">
                                    <span className="text-sm font-medium text-[#5A6376]" data-id="element-88">
                                      {item}
                                    </span>
                                  </div>)}
                              </>;
                    }, [])}
                        </div>
                        {/* Right fade overlay */}
                        <div className="absolute right-0 top-0 bottom-0 w-16 z-10 pointer-events-none bg-gradient-to-l from-[#f0f5ff] to-transparent" data-id="element-89"></div>
                      </motion.div>
                      <style jsx global data-id="element-90">{`
                        @keyframes ticker {
                          0% {
                            transform: translateX(0);
                          }
                          100% {
                            transform: translateX(-50%);
                          }
                        }
                        .ticker-container {
                          width: 100%;
                          overflow: hidden;
                        }
                        .ticker-wrapper {
                          animation: ticker ${isMobile() ? '40s' : '50s'} linear
                            infinite;
                          display: inline-flex;
                          width: max-content;
                          will-change: transform;
                        }
                      `}</style>
                    </div>
                  </div>
                </section>
                {/* CTA #1 */}
                <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f8faff]" data-id="element-91">
                  <div className="max-w-3xl mx-auto text-center" data-id="element-92">
                    <motion.h2 initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.7
              }} className="text-2xl sm:text-3xl font-bold mb-6" data-id="element-93">
                      Book a Call to learn exactly how much you'll save with
                      Pigeon
                    </motion.h2>
                    <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.1,
                duration: 0.7
              }} data-id="element-94">
                      <motion.a href="https://calendly.com/pigeondocuments" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-6 py-3.5 text-base font-medium rounded-full text-white relative overflow-hidden shadow-[0_8px_16px_rgba(46,107,255,0.25)] w-fit" whileHover={{
                  y: -2,
                  boxShadow: '0 12px 20px rgba(46,107,255,0.3)'
                }} whileTap={{
                  scale: 0.98
                }} style={{
                  background: 'linear-gradient(to right, #2E6BFF, #36D1DC)'
                }} data-id="element-95">
                        <span className="relative z-10" data-id="element-96">Book a Call</span>
                        <ArrowRightIcon className="ml-2 h-4 w-4 relative z-10" data-id="element-97" />
                      </motion.a>
                    </motion.div>
                  </div>
                </section>
                {/* Quote Section */}
                <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f0f5ff]" data-id="element-98">
                  <div className="max-w-5xl mx-auto" data-id="element-99">
                    <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.7
              }} className="bg-white rounded-3xl shadow-md p-6 md:p-8 lg:p-10 text-center" data-id="element-100">
                      <div className="flex flex-col items-center" data-id="element-101">
                        {/* Gridiron Logo */}
                        <div className="mb-6" data-id="element-102">
                          <img src="https://i.imgur.com/yw34kZP.png" alt="Gridiron Insurance Logo" className="h-12 w-auto mx-auto" data-id="element-103" />
                        </div>
                        {/* Quote Text */}
                        <div className="max-w-2xl mx-auto" data-id="element-104">
                          <div className="text-2xl md:text-3xl font-bold text-[#0B1220] mb-4 leading-tight" data-id="element-105">
                            "Pigeon has delivered an immediate impact on our
                            operations."
                          </div>
                          <p className="text-base md:text-lg text-[#5A6376] mb-6 leading-relaxed" data-id="element-106">
                            It's streamlined our subjectivity process with
                            seamless document uploads for our producers and an
                            easy-to-use dashboard to monitor and track activity.
                            From onboarding to post-launch support, the entire
                            Pigeon team has been top-notch."
                          </p>
                          {/* Attribution */}
                          <div className="mt-3" data-id="element-107">
                            <div className="font-semibold text-base text-[#0B1220]" data-id="element-108">
                              Jason Green, COO
                            </div>
                            <div className="text-base text-[#5A6376] italic" data-id="element-109">
                              Gridiron Insurance Underwriters
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </section>
                {/* Benefits Section */}
                <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f8faff]" data-id="element-110">
                  <div className="max-w-7xl mx-auto" data-id="element-111">
                    <div className="text-center mb-12" data-id="element-112">
                      <motion.h2 initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.7
                }} className="text-3xl sm:text-4xl font-bold mb-4" data-id="element-113">
                        Pigeon's Benefits
                      </motion.h2>
                      <motion.p initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: 0.1,
                  duration: 0.7
                }} className="text-xl text-[#5A6376] max-w-3xl mx-auto" data-id="element-114">
                        Accelerate Underwriting, Enhance Visibility, and
                        Mitigate Risk
                      </motion.p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-115">
                      {[{
                  title: 'Speed Up Your Underwriting to Write More Business',
                  body: 'Less time wasted means faster binds, quicker bookings of premium, and an ability to handle more submissions without adding headcount.',
                  image: 'https://i.imgur.com/dwtmuBx.png'
                }, {
                  title: 'Gain Team-Wide Tracking and Visibility',
                  body: 'Track and manage all of your subjectivity requests in a centralized dashboard.',
                  image: 'https://i.imgur.com/mhO1dW7.png'
                }, {
                  title: 'Never Miss a Subjectivity – Reduce Risk and E&O Exposure',
                  body: 'Ensure no subjectivities slip through the cracks.',
                  image: 'https://i.imgur.com/Oflr5LZ.png'
                }, {
                  title: 'Seamlessly Integrate – No Disruption to Your Workflow',
                  body: 'Pigeon slots into your existing workflow and tools rather without adding any friction.',
                  image: 'https://i.imgur.com/X8vfuCQ.png'
                }].map((benefit, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: index * 0.1,
                  duration: 0.7
                }} className="bg-white rounded-2xl shadow-md p-8 pb-0 overflow-hidden flex flex-col h-full" data-id="element-116">
                          <div className="flex-grow" data-id="element-117">
                            <h3 className="text-xl font-bold mb-3" data-id="element-118">
                              {benefit.title}
                            </h3>
                            <p className="text-[#5A6376]" data-id="element-119">{benefit.body}</p>
                          </div>
                          <div className="mx-[-2rem] mt-auto" data-id="element-120">
                            <img src={benefit.image} alt={benefit.title} className="w-full h-auto" data-id="element-121" />
                          </div>
                        </motion.div>)}
                    </div>
                  </div>
                </section>
                {/* How Pigeon Works Section */}
                <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f0f5ff]" data-id="element-122">
                  <div className="max-w-7xl mx-auto" data-id="element-123">
                    <motion.h2 initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.7
              }} className="text-3xl sm:text-4xl font-bold text-center mb-12" data-id="element-124">
                      How Pigeon Works
                    </motion.h2>
                    <div className="max-w-3xl mx-auto" data-id="element-125">
                      <div className="relative bg-white rounded-2xl shadow-md overflow-hidden" data-id="element-126">
                        {/* Progress bar at the top */}
                        <div className="flex justify-center gap-2 absolute top-4 left-0 right-0 z-10" data-id="element-127">
                          {slides.map((_, index) => <button key={`indicator-${index}`} onClick={() => setCurrentSlide(index)} className={`h-2 rounded-full transition-all ${currentSlide === index ? 'w-8 bg-[#2E6BFF]' : 'w-2 bg-gray-300'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-128" />)}
                        </div>
                        <motion.div key={`slide-${currentSlide}`} initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} transition={{
                    duration: 0.3
                  }} className="p-6 pt-12" data-id="element-129">
                          <div className="text-center mb-6" data-id="element-130">
                            {slides[currentSlide].hasGradient ? <h3 className="text-xl font-bold mb-2" data-id="element-131">
                                {slides[currentSlide].title.split('\n').map((part, i) => <Fragment key={i} data-id="element-132">
                                      {i > 0 && <br data-id="element-133" />}
                                      {i === 1 ? <>
                                          <span className="bg-gradient-to-r from-[#2E6BFF] to-[#36D1DC] bg-clip-text text-transparent italic" data-id="element-134">
                                            AI-Assisted
                                          </span>
                                          <span data-id="element-135"> Subjectivity Review</span>
                                        </> : part}
                                    </Fragment>)}
                              </h3> : <h3 className="text-xl font-bold mb-2" data-id="element-136">
                                {slides[currentSlide].title.split('\n').map((part, i) => <Fragment key={i} data-id="element-137">
                                      {i > 0 && <br data-id="element-138" />}
                                      {part}
                                    </Fragment>)}
                              </h3>}
                            {slides[currentSlide].subtitle && <p className="text-sm text-[#5A6376]" data-id="element-139">
                                {slides[currentSlide].subtitle}
                              </p>}
                          </div>
                          <div className="h-[32rem] rounded-lg overflow-hidden" data-id="element-140">
                            <AnimatePresence mode="crossfade" initial={false} data-id="element-141">
                              <motion.img key={`slide-image-${currentSlide}`} src={slides[currentSlide].image} alt={`Slide ${currentSlide + 1}`} className="w-full h-full object-contain object-top pt-0 pb-2" initial={{
                          opacity: 0,
                          x: 50,
                          scale: 0.95
                        }} animate={{
                          opacity: 1,
                          x: 0,
                          scale: 1
                        }} exit={{
                          opacity: 0,
                          x: -50,
                          scale: 0.95,
                          position: 'absolute'
                        }} transition={{
                          duration: 0.5,
                          ease: 'easeInOut'
                        }} data-id="element-142" />
                            </AnimatePresence>
                          </div>
                        </motion.div>
                        {/* Navigation buttons */}
                        <button onClick={prevSlide} className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 shadow-md flex items-center justify-center text-[#2E6BFF] z-10 hover:bg-white" aria-label="Previous slide" data-id="element-143">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-144">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" data-id="element-145" />
                          </svg>
                        </button>
                        <button onClick={nextSlide} className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 shadow-md flex items-center justify-center text-[#2E6BFF] z-10 hover:bg-white" aria-label="Next slide" data-id="element-146">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-147">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" data-id="element-148" />
                          </svg>
                        </button>
                        {/* Slide counter */}
                        <div className="absolute bottom-4 left-0 right-0 text-center" data-id="element-149">
                          <span className="bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium" data-id="element-150">
                            {currentSlide + 1} / {slides.length}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
                {/* AI Section */}
                <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f8faff]" data-id="element-151">
                  <div className="max-w-7xl mx-auto" data-id="element-152">
                    <div className="text-center mb-8" data-id="element-153">
                      <motion.h2 initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.7
                }} className="text-3xl sm:text-4xl font-bold mb-4" data-id="element-154">
                        AI-Assisted Subjectivity Review
                      </motion.h2>
                      <motion.p initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: 0.1,
                  duration: 0.7
                }} className="text-xl text-[#5A6376] max-w-3xl mx-auto" data-id="element-155">
                        Pigeon's AI automatically verifies and validates key
                        information
                      </motion.p>
                    </div>
                    <motion.div initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.2,
                duration: 0.7
              }} className="bg-white rounded-2xl shadow-md overflow-hidden" data-id="element-156">
                      <div className="h-96 flex items-center justify-center overflow-hidden rounded-lg" data-id="element-157">
                        <img src="https://i.imgur.com/6Cciayx.gif" alt="AI-Assisted Subjectivity Review in action" className="w-full h-full object-contain" data-id="element-158" />
                      </div>
                    </motion.div>
                  </div>
                </section>
                {/* PigeonAPI Component */}
                <section id="integrations" data-id="element-159">
                  <PigeonAPI data-id="element-160" />
                </section>
                {/* E-Signature Section */}
                <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#f8faff]" data-id="element-161">
                  <div className="max-w-7xl mx-auto" data-id="element-162">
                    <div className="grid md:grid-cols-2 gap-8 items-center" data-id="element-163">
                      <motion.div initial={{
                  opacity: 0,
                  x: -20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.7
                }} className="bg-white rounded-2xl shadow-md overflow-hidden" data-id="element-164">
                        <div className="flex justify-center" data-id="element-165">
                          <img src="https://i.imgur.com/nJCSug5.png" alt="Pigeon E-Signature interface example" className="w-full h-auto" data-id="element-166" />
                        </div>
                      </motion.div>
                      <motion.div initial={{
                  opacity: 0,
                  x: 20
                }} whileInView={{
                  opacity: 1,
                  x: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.7
                }} data-id="element-167">
                        <div className="inline-block px-3 py-1 bg-[#F0F5FF] text-[#2E6BFF] rounded-full text-sm font-medium mb-4" data-id="element-168">
                          Key Feature
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-bold mb-4" data-id="element-169">
                          Pigeon E-Signature
                        </h2>
                        <p className="text-xl text-[#5A6376] mb-6" data-id="element-170">
                          Best in class e-signature at no additional cost
                        </p>
                        <motion.a href="#" className="inline-flex items-center text-[#2E6BFF] font-medium" whileHover={{
                    x: 5
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-171">
                          Learn more
                          <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-172" />
                        </motion.a>
                      </motion.div>
                    </div>
                  </div>
                </section>
                {/* Enterprise-grade security & compliance section */}
                <div id="security" data-id="element-173">
                  <SecuritySection data-id="element-174" />
                </div>
              </main>
              <Footer data-id="element-175" />
            </div>} data-id="element-2" />
        <Route path="/success" element={<SuccessPage data-id="element-177" />} data-id="element-176" />
      </Routes>
    </BrowserRouter>;
}